import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MediaFile, FileManagerProps, FileUploadProgress, FileManagerConfig } from '../../types/index';
import { apiClient } from '../../services/apiClient';
import styles from './FileManager.module.css';

const defaultConfig: FileManagerConfig = {
  maxFileSize: 100 * 1024 * 1024, // 100MB
  allowedTypes: ['image/*', 'video/*', 'audio/*', 'application/pdf', '.doc', '.docx'],
  multiple: true,
  showPreview: true,
  showMetadata: true,
  enableDragDrop: true,
  enableCrop: false,
  thumbnailSize: 150,
};

export const FileManager: React.FC<FileManagerProps> = ({
  config = {},
  onFileSelect,
  onFileUpload,
  onFileDelete,
  selectedFiles = [],
  className,
  hasDeleteMany = false,
}) => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [internalSelectedFiles, setInternalSelectedFiles] = useState<MediaFile[]>([]);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [bulkSelectedFiles, setBulkSelectedFiles] = useState<Set<string>>(new Set());
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mergedConfig = { ...defaultConfig, ...config };

  // Load files
  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.getMediaFiles({
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined,
      });
      
      setFiles(response.data as MediaFile[] || []);
      setTotalPages((response as any)?.pagination?.pages || 1);
    } catch (err: any) {
      setError(err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Handle file selection
  const handleFileSelect = (file: MediaFile) => {
    if (mergedConfig.selectionMode) {
      // In selection mode, manage internal selection state
      const isSelected = internalSelectedFiles.some(f => f.id === file.id);
      let newSelection: MediaFile[];
      
      if (isSelected) {
        newSelection = internalSelectedFiles.filter(f => f.id !== file.id);
      } else {
        newSelection = mergedConfig.multiple 
          ? [...internalSelectedFiles, file]
          : [file];
      }
      
      setInternalSelectedFiles(newSelection);
      
      // Notify parent component about selection changes
      if (onFileSelect) {
        onFileSelect(newSelection);
      }
    } else {
      // Original logic for non-selection mode
      if (onFileSelect) {
        const isSelected = selectedFiles.includes(file.id);
        const newSelection = isSelected
          ? selectedFiles.filter(id => id !== file.id)
          : mergedConfig.multiple
          ? [...selectedFiles, file.id]
          : [file.id];
        
        const selectedFileObjects = files.filter(f => newSelection.includes(f.id));
        onFileSelect(selectedFileObjects);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = async (filesToUpload: File[]) => {
    const progressItems: FileUploadProgress[] = filesToUpload.map(file => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    
    setUploadProgress(progressItems);

    const results: MediaFile[] = [];
    
    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      
      try {
        // Update progress to uploading
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { ...item, status: 'uploading', progress: 0 } : item
        ));

        const result = await apiClient.uploadFile(file);
        
        // Update progress to success
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { ...item, status: 'success', progress: 100, result } : item
        ));
        
        results.push(result);
      } catch (error: any) {
        // Update progress to error
        setUploadProgress(prev => prev.map((item, index) => 
          index === i ? { 
            ...item, 
            status: 'error', 
            progress: 0, 
            error: error.message || 'Upload failed' 
          } : item
        ));
      }
    }

    // Clear progress after 3 seconds
    setTimeout(() => {
      setUploadProgress([]);
    }, 3000);

    // Reload files and notify parent
    if (results.length > 0) {
      await loadFiles();
      if (onFileUpload) {
        onFileUpload(results);
      }
    }
  };

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFileUpload(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  // Handle file deletion
  const handleFileDelete = async (fileId: string) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await apiClient.deleteMediaFile(fileId);
        await loadFiles();
        if (onFileDelete) {
          onFileDelete(fileId);
        }
      } catch (error: any) {
        setError(error.message || 'Failed to delete file');
      }
    }
  };

  // Handle bulk file selection
  const handleBulkFileSelect = (fileId: string, isSelected: boolean) => {
    setBulkSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(fileId);
      } else {
        newSet.delete(fileId);
      }
      return newSet;
    });
  };

  // Handle select all / deselect all
  const handleSelectAll = () => {
    if (bulkSelectedFiles.size === files.length) {
      setBulkSelectedFiles(new Set());
    } else {
      setBulkSelectedFiles(new Set(files.map(f => f.id)));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const selectedIds = Array.from(bulkSelectedFiles);
    if (selectedIds.length === 0) {
      alert('Please select files to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedIds.length} file(s)? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        const result = await apiClient.bulkDeleteMediaFiles(selectedIds);
        
        if (result.failed.length > 0) {
          const failedMessage = `Failed to delete ${result.failed.length} file(s): ${result.failed.map(f => f.error).join(', ')}`;
          setError(failedMessage);
        }
        
        if (result.deleted > 0) {
          // Clear selection and reload files
          setBulkSelectedFiles(new Set());
          await loadFiles();
          
          // Notify parent if callback provided
          if (onFileDelete) {
            selectedIds.forEach(id => onFileDelete(id));
          }
        }
      } catch (error: any) {
        setError(error.message || 'Failed to delete files');
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle bulk delete mode
  const toggleBulkDeleteMode = () => {
    setBulkDeleteMode(!bulkDeleteMode);
    setBulkSelectedFiles(new Set());
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
    return '📁';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render file item
  const renderFileItem = (file: MediaFile) => {
    const isSelected = mergedConfig.selectionMode 
      ? internalSelectedFiles.some(f => f.id === file.id)
      : selectedFiles.includes(file.id);
    const isBulkSelected = bulkSelectedFiles.has(file.id);
    const isImage = file.mimeType.startsWith('image/');
    const thumbnailUrl = file.thumbnails.find(t => t.type === 'thumbnail')?.url || file.url;

    const handleItemClick = () => {
      if (bulkDeleteMode) {
        handleBulkFileSelect(file.id, !isBulkSelected);
      } else {
        handleFileSelect(file);
      }
    };

    return (
      <div
        key={file.id}
        className={`${styles.fileItem} ${viewMode === 'list' ? styles.fileItemList : ''} ${
          isSelected ? styles.selected : ''
        } ${isBulkSelected ? styles.bulkSelected : ''} ${
          mergedConfig.selectionMode || bulkDeleteMode ? styles.selectable : ''
        }`}
        onClick={handleItemClick}
      >
        <div className={`${styles.filePreview} ${viewMode === 'list' ? styles.filePreviewList : ''}`}>
          {/* Selection indicators */}
          {mergedConfig.selectionMode && (
            <div className={`${styles.selectionIndicator} ${isSelected ? styles.selectedIndicator : ''}`}>
              {isSelected ? '✓' : ''}
            </div>
          )}
          {bulkDeleteMode && (
            <div className={`${styles.bulkSelectionIndicator} ${isBulkSelected ? styles.bulkSelectedIndicator : ''}`}>
              <input
                type="checkbox"
                checked={isBulkSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  handleBulkFileSelect(file.id, e.target.checked);
                }}
                className={styles.bulkCheckbox}
              />
            </div>
          )}
          {isImage ? (
            <img src={thumbnailUrl} alt={file.alt || file.filename} />
          ) : (
            <span className={`${styles.fileIcon} ${viewMode === 'list' ? styles.fileIconList : ''}`}>
              {getFileIcon(file.mimeType)}
            </span>
          )}
        </div>
        
        <div className={`${styles.fileName} ${viewMode === 'list' ? styles.fileNameList : ''}`}>
          {file.filename}
        </div>
        
        {mergedConfig.showMetadata && (
          <div className={`${styles.fileInfo} ${viewMode === 'list' ? styles.fileInfoList : ''}`}>
            <span>{formatFileSize(file.size)}</span>
            <span>{file.type}</span>
            {file.width && file.height && (
              <span>{file.width}×{file.height}</span>
            )}
          </div>
        )}
        
        {!bulkDeleteMode && (
          <div className={styles.fileActions}>
            <button
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                window.open(file.url, '_blank');
              }}
            >
              View
            </button>
            <button
              className={`${styles.actionButton} ${styles.danger}`}
              onClick={(e) => {
                e.stopPropagation();
                handleFileDelete(file.id);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${styles.fileManager} ${className || ''}`}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button
            className={styles.uploadButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || bulkDeleteMode}
          >
            📁 Upload Files
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple={mergedConfig.multiple}
            accept={mergedConfig.allowedTypes?.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          
          {!bulkDeleteMode && (
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          )}
          
          {/* Bulk delete controls */}
          {hasDeleteMany && (
            <>
              <button
                className={`${styles.bulkDeleteToggle} ${bulkDeleteMode ? styles.active : ''}`}
                onClick={toggleBulkDeleteMode}
                disabled={loading}
              >
                {bulkDeleteMode ? '❌ Cancel' : '🗑️ Bulk Delete'}
              </button>
              
              {bulkDeleteMode && (
                <>
                  <button
                    className={styles.selectAllButton}
                    onClick={handleSelectAll}
                    disabled={loading || files.length === 0}
                  >
                    {bulkSelectedFiles.size === files.length ? '☑️ Deselect All' : '☐ Select All'}
                  </button>
                  
                  <button
                    className={`${styles.bulkDeleteButton} ${styles.danger}`}
                    onClick={handleBulkDelete}
                    disabled={loading || bulkSelectedFiles.size === 0}
                  >
                    🗑️ Delete Selected ({bulkSelectedFiles.size})
                  </button>
                </>
              )}
            </>
          )}
        </div>
        
        <div className={styles.toolbarRight}>
          {!bulkDeleteMode && (
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'grid' ? styles.active : ''}`}
                onClick={() => setViewMode('grid')}
              >
                ⊞
              </button>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'list' ? styles.active : ''}`}
                onClick={() => setViewMode('list')}
              >
                ☰
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.mainArea}>
          {/* Drag and drop zone */}
          {mergedConfig.enableDragDrop && (
            <div
              className={`${styles.dragDropZone} ${dragOver ? styles.dragOver : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{ display: files.length === 0 && !loading ? 'block' : 'none' }}
            >
              <div>📁</div>
              <div>Drag and drop files here or click to upload</div>
            </div>
          )}

          {/* File grid */}
          <div className={styles.fileGrid}>
            {loading ? (
              <div className={styles.loading}>Loading files...</div>
            ) : error ? (
              <div className={styles.error}>{error}</div>
            ) : files.length === 0 ? (
              <div className={styles.empty}>
                <div className={styles.emptyIcon}>📁</div>
                <div>No files found</div>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? styles.gridView : styles.listView}>
                {files.map(renderFileItem)}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`${styles.paginationButton} ${page === currentPage ? styles.active : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              
              <button
                className={styles.paginationButton}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload progress */}
      {uploadProgress.length > 0 && (
        <div className={styles.uploadProgress}>
          {uploadProgress.map((item, index) => (
            <div key={index} className={styles.progressItem}>
              <span>{item.file.name}</span>
              <div className={styles.progressBar}>
                <div
                  className={`${styles.progressFill} ${
                    item.status === 'error' ? styles.error : 
                    item.status === 'success' ? styles.success : ''
                  }`}
                  style={{ width: `${item.progress}%` }}
                />
              </div>
              <span>
                {item.status === 'error' ? item.error : 
                 item.status === 'success' ? 'Done' : 
                 `${item.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
